import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Form, List, Loader, Message, Modal, TextArea,
} from 'semantic-ui-react';

import {
  DIALOG_CANCEL_LABEL,
  NOTES_DIALOG_HEADER,
  NOTES_EMPTY_MESSAGE,
  NOTES_LOADING_MESSAGE,
  NOTE_CONFIRM_LABEL,
  NOTE_FIELD_LABEL,
  NOTE_MAX_LENGTH,
  NOTE_REQUIRED_MESSAGE,
  NOTE_TOO_LONG_MESSAGE,
} from '../../constants/onboardingQueue';
import { wallClockDateTime } from '../../utilities/onboardingQueue';
import './NotesDialog.css';

const noteValidationError = note => {
  if (!note.trim()) return NOTE_REQUIRED_MESSAGE;
  if (note.length > NOTE_MAX_LENGTH) return NOTE_TOO_LONG_MESSAGE;
  return '';
};

const NotesDialog = ({
  record, notes, notesLoading, notesError, loading, errorMessage, onClose, onConfirm,
}) => {
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState('');
  const [appending, setAppending] = useState(false);

  const busy = loading || appending;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleConfirm = async() => {
    if (busy) return;
    const invalid = noteValidationError(note);
    setValidationError(invalid);
    if (invalid) return;

    setAppending(true);
    const result = await onConfirm({ note: note.trim() });
    setAppending(false);
    if (!result || result.success) setNote('');
  };

  const remaining = NOTE_MAX_LENGTH - note.length;

  return (
    <Modal
      open
      size='small'
      onClose={handleClose}
      closeOnEscape={!busy}
      closeOnDimmerClick={false}
      data-testid='queue-notes-dialog'
    >
      <Modal.Header>{NOTES_DIALOG_HEADER}</Modal.Header>
      <Modal.Content>
        <p data-testid='queue-notes-dealer'>{record.dealerName}</p>

        {notesLoading && (
          <Loader active inline='centered' data-testid='queue-notes-loading'>
            {NOTES_LOADING_MESSAGE}
          </Loader>
        )}

        {!notesLoading && notesError && (
          <Message negative data-testid='queue-notes-load-error'>
            <p>{notesError}</p>
          </Message>
        )}

        {!notesLoading && !notesError && notes.length === 0 && (
          <Message info data-testid='queue-notes-empty'>
            <p>{NOTES_EMPTY_MESSAGE}</p>
          </Message>
        )}

        {!notesLoading && !notesError && notes.length > 0 && (
          <div
            className='onboarding-queue-notes-scroll'
            data-testid='queue-notes-scroll'
          >
            <List data-testid='queue-notes-list'>
              {notes.map(entry => (
                <List.Item
                  key={entry.id}
                  className='onboarding-queue-note'
                  data-testid='queue-note-entry'
                >
                  <List.Content>
                    <List.Header data-testid='queue-note-author'>
                      {entry.createdBy}
                      {' — '}
                      <span data-testid='queue-note-timestamp'>
                        {wallClockDateTime(entry.createdAt)}
                      </span>
                    </List.Header>
                    <List.Description data-testid='queue-note-text'>
                      {entry.note}
                    </List.Description>
                  </List.Content>
                </List.Item>
              ))}
            </List>
          </div>
        )}

        {!notesLoading && !notesError && (
          <Form data-testid='queue-notes-form'>
            <Form.Field required error={Boolean(validationError)}>
              <label htmlFor='queue-note-input'>{NOTE_FIELD_LABEL}</label>
              <TextArea
                id='queue-note-input'
                value={note}
                disabled={busy}
                data-testid='queue-note-input'
                onChange={(event, { value }) => {
                  setNote(value);
                  setValidationError(
                    value.length > NOTE_MAX_LENGTH ? NOTE_TOO_LONG_MESSAGE : ''
                  );
                }}
              />
            </Form.Field>
            <div data-testid='queue-note-remaining'>
              {`${remaining} of ${NOTE_MAX_LENGTH} characters remaining`}
            </div>
          </Form>
        )}

        {validationError && (
          <Message negative size='mini' data-testid='queue-notes-validation'>
            <p>{validationError}</p>
          </Message>
        )}

        {errorMessage && (
          <Message negative data-testid='queue-notes-error'>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={busy} data-testid='queue-notes-close'>
          {DIALOG_CANCEL_LABEL}
        </Button>
        {!notesLoading && !notesError && (
          <Button
            primary
            loading={busy}
            disabled={busy}
            data-testid='queue-note-confirm'
            onClick={handleConfirm}
          >
            {NOTE_CONFIRM_LABEL}
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

NotesDialog.propTypes = {
  record: PropTypes.shape({ dealerName: PropTypes.string }).isRequired,
  notes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    note: PropTypes.string,
    createdBy: PropTypes.string,
    createdAt: PropTypes.string,
  })),
  notesLoading: PropTypes.bool,
  notesError: PropTypes.string,
  loading: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

NotesDialog.defaultProps = {
  notes: [],
  notesLoading: false,
  notesError: null,
  loading: false,
  errorMessage: null,
};

export default NotesDialog;
